/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { EditorThemeClasses } from 'lexical';

import './DarkEditorTheme.css';

const theme: EditorThemeClasses = {
   blockCursor: 'DarkEditorTheme__blockCursor',
   characterLimit: 'DarkEditorTheme__characterLimit',
   code: 'DarkEditorTheme__code',
   codeHighlight: {
      atrule: 'DarkEditorTheme__tokenAttr',
      attr: 'DarkEditorTheme__tokenAttr',
      boolean: 'DarkEditorTheme__tokenProperty',
      builtin: 'DarkEditorTheme__tokenSelector',
      cdata: 'DarkEditorTheme__tokenComment',
      char: 'DarkEditorTheme__tokenSelector',
      class: 'DarkEditorTheme__tokenFunction',
      'class-name': 'DarkEditorTheme__tokenFunction',
      comment: 'DarkEditorTheme__tokenComment',
      constant: 'DarkEditorTheme__tokenProperty',
      deleted: 'DarkEditorTheme__tokenProperty',
      doctype: 'DarkEditorTheme__tokenComment',
      entity: 'DarkEditorTheme__tokenOperator',
      function: 'DarkEditorTheme__tokenFunction',
      important: 'DarkEditorTheme__tokenVariable',
      inserted: 'DarkEditorTheme__tokenSelector',
      keyword: 'DarkEditorTheme__tokenAttr',
      namespace: 'DarkEditorTheme__tokenVariable',
      number: 'DarkEditorTheme__tokenProperty',
      operator: 'DarkEditorTheme__tokenOperator',
      prolog: 'DarkEditorTheme__tokenComment',
      property: 'DarkEditorTheme__tokenProperty',
      punctuation: 'DarkEditorTheme__tokenPunctuation',
      regex: 'DarkEditorTheme__tokenVariable',
      selector: 'DarkEditorTheme__tokenSelector',
      string: 'DarkEditorTheme__tokenSelector',
      symbol: 'DarkEditorTheme__tokenProperty',
      tag: 'DarkEditorTheme__tokenProperty',
      url: 'DarkEditorTheme__tokenOperator',
      variable: 'DarkEditorTheme__tokenVariable',
   },
   embedBlock: {
      base: 'DarkEditorTheme__embedBlock',
      focus: 'DarkEditorTheme__embedBlockFocus',
   },
   hashtag: 'DarkEditorTheme__hashtag',
   heading: {
      h1: 'DarkEditorTheme__h1',
      h2: 'DarkEditorTheme__h2',
      h3: 'DarkEditorTheme__h3',
      h4: 'DarkEditorTheme__h4',
      h5: 'DarkEditorTheme__h5',
      h6: 'DarkEditorTheme__h6',
   },
   image: 'editor-image',
   link: 'DarkEditorTheme__link',
   list: {
      listitem: 'DarkEditorTheme__listItem',
      listitemChecked: 'DarkEditorTheme__listItemChecked',
      listitemUnchecked: 'DarkEditorTheme__listItemUnchecked',
      nested: {
         listitem: 'DarkEditorTheme__nestedListItem',
      },
      olDepth: [
         'DarkEditorTheme__ol1',
         'DarkEditorTheme__ol2',
         'DarkEditorTheme__ol3',
         'DarkEditorTheme__ol4',
         'DarkEditorTheme__ol5',
      ],
      ul: 'DarkEditorTheme__ul',
   },
   ltr: 'DarkEditorTheme__ltr',
   mark: 'DarkEditorTheme__mark',
   markOverlap: 'DarkEditorTheme__markOverlap',
   paragraph: 'DarkEditorTheme__paragraph',
   quote: 'DarkEditorTheme__quote',
   rtl: 'DarkEditorTheme__rtl',
   table: 'DarkEditorTheme__table',
   tableAddColumns: 'DarkEditorTheme__tableAddColumns',
   tableAddRows: 'DarkEditorTheme__tableAddRows',
   tableCell: 'DarkEditorTheme__tableCell',
   tableCellActionButton: 'DarkEditorTheme__tableCellActionButton',
   tableCellActionButtonContainer:
      'DarkEditorTheme__tableCellActionButtonContainer',
   tableCellEditing: 'DarkEditorTheme__tableCellEditing',
   tableCellHeader: 'DarkEditorTheme__tableCellHeader',
   tableCellPrimarySelected: 'DarkEditorTheme__tableCellPrimarySelected',
   tableCellResizer: 'DarkEditorTheme__tableCellResizer',
   tableCellSelected: 'DarkEditorTheme__tableCellSelected',
   tableCellSortedIndicator: 'DarkEditorTheme__tableCellSortedIndicator',
   tableResizeRuler: 'DarkEditorTheme__tableCellResizeRuler',
   tableSelected: 'DarkEditorTheme__tableSelected',
   text: {
      bold: 'DarkEditorTheme__textBold',
      code: 'DarkEditorTheme__textCode',
      italic: 'DarkEditorTheme__textItalic',
      strikethrough: 'DarkEditorTheme__textStrikethrough',
      subscript: 'DarkEditorTheme__textSubscript',
      superscript: 'DarkEditorTheme__textSuperscript',
      underline: 'DarkEditorTheme__textUnderline',
      underlineStrikethrough: 'DarkEditorTheme__textUnderlineStrikethrough',
   },
};

export default theme;
